import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, LayoutDashboard, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth-context";

type NavLink = { label: string; href: string; external?: boolean };
const LINKS: NavLink[] = [
  { label: "Features", href: "/#features" },
  { label: "Modes",    href: "/#modes" },
  { label: "Pricing",  href: "/subscribe" }
];

export function SiteNav({ hidePointsPill = false }: { hidePointsPill?: boolean }) {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const loc = useLocation();
  const { session, access, signOut } = useAuth();

  React.useEffect(() => { setOpen(false); }, [loc.pathname]);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const points = access?.profile.total_points ?? 0;

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="mx-auto mt-4 max-w-6xl px-4">
        <nav className={cn(
          "flex items-center justify-between h-14 px-3 pl-4 pr-3",
          "rounded-full border border-line bg-bg/60 backdrop-blur-xl transition-all",
          scrolled && "bg-bg/85 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.7)]"
        )}>
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <img src="/logo.png" alt="" className="h-7 w-7 rounded-md" />
            <span className="font-bold tracking-[0.15em] text-sm">ARABISE</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {LINKS.map(l => (
              <a
                key={l.label}
                href={l.href}
                {...(l.external ? { target: "_blank", rel: "noopener" } : {})}
                className="px-3 py-1.5 text-sm text-fg-2 hover:text-fg transition-colors rounded-full"
              >
                {l.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-2">
            {session ? (
              <>
                {access && !hidePointsPill && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-accent-lo bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                    {points.toLocaleString()} pts
                  </span>
                )}
                <Button variant="primary" size="sm" asChild>
                  <Link to="/dashboard"><LayoutDashboard className="w-4 h-4" /> Dashboard</Link>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => signOut()} aria-label="Sign out">
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" asChild><Link to="/login">Sign in</Link></Button>
                <Button variant="primary" size="sm" asChild><Link to="/login">Sign up</Link></Button>
              </>
            )}
          </div>

          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-full text-fg-2 hover:text-fg hover:bg-bg-3 transition-colors"
            onClick={() => setOpen(v => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </nav>

        {open && (
          <div className="mt-2 md:hidden rounded-2xl border border-line bg-bg/95 backdrop-blur-xl p-2">
            {LINKS.map(l => (
              <a
                key={l.label}
                href={l.href}
                {...(l.external ? { target: "_blank", rel: "noopener" } : {})}
                className="block px-4 py-2.5 text-sm text-fg-2 hover:text-fg hover:bg-bg-3 rounded-lg"
              >
                {l.label}
              </a>
            ))}
            <div className="grid grid-cols-2 gap-2 p-2 pt-3 border-t border-line mt-2">
              {session ? (
                <>
                  <Button variant="primary" size="sm" asChild><Link to="/dashboard">Dashboard</Link></Button>
                  <Button variant="outline" size="sm" onClick={() => signOut()}>Sign out</Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="sm" asChild><Link to="/login">Sign in</Link></Button>
                  <Button variant="primary" size="sm" asChild><Link to="/login">Sign up</Link></Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
